/**
 * Public event slug helpers (ClickUp 86d45muck).
 *
 * `/events/<param>` is shared by two very different pages: the authed member
 * event page (`<param>` is a UUID) and the public, sessionless ticket page
 * (`<param>` is a slug). The root gate lists in `routes/__root.tsx` are
 * prefix-based, so they cannot un-gate `/events/<slug>` without also
 * un-gating `/events` and `/events/<uuid>`. These predicates are the single
 * place that decides which shape a path has. The DB enforces the same slug
 * grammar (`community_events_slug_chk`) and forbids UUID-shaped slugs.
 */

export const EVENT_SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const EVENT_SLUG_MIN = 3;
export const EVENT_SLUG_MAX = 64;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EVENT_PATH_RE = /^\/events\/([^/?#]+)\/?$/;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

export function isValidEventSlug(value: string): boolean {
  return (
    value.length >= EVENT_SLUG_MIN &&
    value.length <= EVENT_SLUG_MAX &&
    EVENT_SLUG_RE.test(value) &&
    !isUuid(value)
  );
}

/**
 * True only for `/events/<slug>` where `<slug>` is a valid, non-UUID slug.
 * `/events`, `/events/`, `/events/<uuid>` and deeper paths are false.
 */
export function isPublicEventSlugPath(pathname: string): boolean {
  const match = EVENT_PATH_RE.exec(pathname);
  if (!match) return false;
  const segment = match[1];
  return !isUuid(segment) && isValidEventSlug(segment);
}

export function publicEventUrl(
  slug: string,
  origin: string = window.location.origin,
): string {
  return `${origin}/events/${slug}`;
}
